import type { CSSProperties, ReactNode } from "react";
import { memo, useEffect, useState } from "react";
import FrameComponent from "react-frame-component";
import type { FrameComponentProps } from "react-frame-component";

const responsiveFrame: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
};

export interface ResponsiveFrameProps extends Omit<FrameComponentProps, "ref"> {
  children: ReactNode;
  shouldAdaptHeightToContentSize?: boolean;
}

const getIframe = (frameNode: any) => frameNode?.node || frameNode;

const getDoc = (iframe: any) => iframe?.contentDocument;

const updateHeight = (iframe: any) => {
  try {
    const doc = getDoc(iframe);
    if (doc && doc.body && doc.documentElement) {
      // Force reflow by reading offsetHeight
      void doc.body.offsetHeight;
      void doc.documentElement.offsetHeight;
      // Use requestAnimationFrame to ensure layout is settled
      window.requestAnimationFrame(() => {
        const body = doc.body;
        const html = doc.documentElement;
        const frameContent = doc.querySelector(".frame-content");
        let newHeight;
        if (frameContent) {
          newHeight =
            (frameContent.offsetHeight || frameContent.scrollHeight) + 5;
        } else {
          newHeight =
            Math.max(
              body.scrollHeight,
              html.scrollHeight,
              body.offsetHeight,
              html.offsetHeight,
              body.clientHeight,
              html.clientHeight,
            ) + 5;
        }
        iframe.style.height = `${newHeight}px`;
      });
    }
  } catch (err) {
    console.error("ResponsiveFrame: Failed to update height", err);
  }
};

const setupObservers = (iframe: any, updateHeightFn: () => void) => {
  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;
  const doc = getDoc(iframe);
  if (doc && doc.body && window.ResizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      updateHeightFn();
    });
    resizeObserver.observe(doc.body);
  }
  if (doc && doc.body && window.MutationObserver) {
    mutationObserver = new MutationObserver(() => {
      updateHeightFn();
    });
    mutationObserver.observe(doc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  }
  return { resizeObserver, mutationObserver };
};

export const ResponsiveFrame = memo(
  ({
    children,
    style,
    shouldAdaptHeightToContentSize,
    ...rest
  }: ResponsiveFrameProps) => {
    const [frameNode, setFrameNode] = useState<any>(null);
    const frameRef = (node: any) => {
      setFrameNode(node);
    };

    useEffect(() => {
      if (!shouldAdaptHeightToContentSize || !frameNode) {
        return;
      }
      const iframe = getIframe(frameNode);

      let resizeObserver: ResizeObserver | null = null;
      let mutationObserver: MutationObserver | null = null;

      const onLoad = () => {
        updateHeight(iframe);
        const observers = setupObservers(iframe, () => updateHeight(iframe));
        resizeObserver = observers.resizeObserver;
        mutationObserver = observers.mutationObserver;
      };

      iframe?.addEventListener("load", onLoad);
      // In case iframe is already loaded
      if (iframe?.contentDocument?.readyState === "complete") {
        onLoad();
      }
      return () => {
        iframe?.removeEventListener("load", onLoad);
        if (resizeObserver) resizeObserver.disconnect();
        if (mutationObserver) mutationObserver.disconnect();
      };
    }, [frameNode, shouldAdaptHeightToContentSize]);

    return (
      <FrameComponent
        ref={frameRef}
        style={{ ...responsiveFrame, ...style }}
        {...rest}
      >
        {children}
      </FrameComponent>
    );
  },
);

ResponsiveFrame.displayName = "ResponsiveFrame";
